import * as React from 'react';

import { cn } from '@/lib/utils';

function Table({ className, ...props }: Readonly<React.ComponentProps<'table'>>) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto rotaready:rounded-t-[4px]"
    >
      <table
        data-slot="table"
        className={cn(
          'w-full caption-bottom text-[length:var(--body-font-size)] rotaready:bg-background rotaready:font-sans rotaready:leading-[var(--body-line-height)] rotaready:text-foreground',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: Readonly<React.ComponentProps<'thead'>>) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        '[&_tr]:border-b rotaready:bg-[var(--grey-20)] rotaready:[&_tr]:border-0 rotaready:[&_tr]:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: Readonly<React.ComponentProps<'tbody'>>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        '[&_tr:last-child]:border-0 rotaready:[&_tr:not(:only-child):last-child]:border-b',
        className,
      )}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: Readonly<React.ComponentProps<'tfoot'>>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: Readonly<React.ComponentProps<'tr'>>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted rotaready:border-[var(--grey-20)]',
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: Readonly<React.ComponentProps<'th'>>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 px-2 text-left align-middle whitespace-nowrap text-[length:var(--body-font-size)] text-[var(--table-header-foreground)] font-[var(--table-header-font-weight)] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] rotaready:h-[50px] rotaready:first:pl-[15px] rotaready:last:pr-[15px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: Readonly<React.ComponentProps<'td'>>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] rotaready:h-[40px] rotaready:py-0 rotaready:first:pl-[15px] rotaready:last:pr-[15px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: Readonly<React.ComponentProps<'caption'>>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
